import {
  ActionIcon,
  Button,
  CopyButton,
  Group,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Tooltip
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconCheck, IconCopy, IconDownload, IconMail, IconPrinter } from "@tabler/icons-react";
import { QRCodeSVG } from "qrcode.react";
import { useRef } from "react";

type QRCodeModalProperties = {
  regulamento: TRegulamentoIdeias;
  opened: boolean;
  onClose: () => void;
};

/**
 * Generate the quick idea URL for a given regulamento
 * @param regulamentoId
 */
function generateQuickIdeaUrl(regulamentoId: string): string {
  const baseUrl = window.location.origin;
  return `${baseUrl}/?quickIdea=true&regulamentoId=${regulamentoId}`;
}

/**
 * QRCodeModal - Modal to display and share QR Code for quick idea submission
 * @param root0
 * @param root0.regulamento
 * @param root0.opened
 * @param root0.onClose
 */
const QRCodeModal = ({ regulamento, opened, onClose }: QRCodeModalProperties) => {
  const qrReference = useRef<HTMLDivElement>(null);
  const url = generateQuickIdeaUrl(regulamento.id);

  /**
   * Download QR Code as PNG
   */
  const handleDownload = () => {
    const svg = qrReference.current?.querySelector("svg");
    if (!svg) return;

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    const data = new XMLSerializer().serializeToString(svg);
    const img = new Image();

    canvas.width = 300;
    canvas.height = 300;

    img.addEventListener("load", () => {
      context?.drawImage(img, 0, 0, 300, 300);
      const pngUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = `qrcode-${regulamento.nome}.png`;
      link.href = pngUrl;
      link.click();

      showNotification({
        message: "QR Code baixado com sucesso!",
        color: "green"
      });
    });

    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(data)));
  };

  /**
   * Print QR Code
   */
  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const svg = qrReference.current?.querySelector("svg");
    if (!svg) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>QR Code - ${regulamento.nome}</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              font-family: sans-serif;
            }
            h1 { font-size: 24px; margin-bottom: 20px; }
            p { font-size: 14px; color: #666; margin-top: 20px; }
          </style>
        </head>
        <body>
          <h1>Cadastrar Ideia</h1>
          ${svg.outerHTML}
          <p>${regulamento.nome}</p>
          <p style="font-size: 12px;">Escaneie o QR Code para cadastrar uma ideia</p>
          <script>window.onload = () => { window.print(); window.close(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  /**
   * Send via email (mailto)
   */
  const handleEmail = () => {
    const subject = encodeURIComponent(`Cadastrar Ideia - ${regulamento.nome}`);
    const body = encodeURIComponent(
      `Olá!\n\nUse o link abaixo para cadastrar uma ideia:\n\n${url}\n\nOu escaneie o QR Code anexo.`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`, "_blank");
  };

  return (
    <Modal opened={opened} onClose={onClose} title="QR Code para Cadastro de Ideias" size="md" centered>
      <Stack align="center" gap="lg">
        <Text size="sm" c="dimmed" ta="center">
          Compartilhe este QR Code para que colaboradores possam cadastrar ideias pelo celular.
        </Text>

        <div ref={qrReference} style={{ padding: 16, background: "white", borderRadius: 8 }}>
          <QRCodeSVG value={url} size={200} level="H" includeMargin />
        </div>

        <Text size="xs" c="dimmed" ta="center" fw={500}>
          {regulamento.nome}
        </Text>

        <Group gap="xs">
          <TextInput
            value={url}
            readOnly
            style={{ flex: 1, minWidth: 200 }}
            rightSection={
              <CopyButton value={url}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? "Copiado!" : "Copiar link"}>
                    <ActionIcon color={copied ? "teal" : "gray"} onClick={copy} variant="subtle">
                      {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            }
          />
        </Group>

        <SimpleGrid cols={{ base: 1, xs: 3 }} spacing="xs" w="100%">
          <Button variant="light" leftSection={<IconDownload size={16} />} onClick={handleDownload} fullWidth>
            Baixar
          </Button>
          <Button variant="light" leftSection={<IconPrinter size={16} />} onClick={handlePrint} fullWidth>
            Imprimir
          </Button>
          <Button variant="light" leftSection={<IconMail size={16} />} onClick={handleEmail} fullWidth>
            Email
          </Button>
        </SimpleGrid>
      </Stack>
    </Modal>
  );
};

export default QRCodeModal;
